import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@lift-admin/utils/apiClient';
import { logAdminAction } from '@lift-admin/utils/actionLogTrack';
import { buildDetails, diffFields, humanizeValue, formatIdWithName, type FieldSpec } from '@lift-admin/utils/logChangeFormatter';

const REPORT_CONFIG_FIELD_SPECS: FieldSpec[] = [
  { key: 'platform', label: 'Platform' },
  { key: 'report_type', label: 'Report Type' },
  { key: 'granularity_level', label: 'Granularity' },
  { key: 'metric_type', label: 'Metric Type' },
  { key: 'cadence', label: 'Cadence' },
  { key: 'breakdown', label: 'Breakdown' },
  { key: 'use_fiscal_calendar', label: 'Uses Fiscal Calendar', type: 'boolean', boolStyle: 'yesNo' },
];
import { REPORT_CONFIG_BASE_URL, API_ENDPOINTS } from '../api';
import type { ReportConfiguration, ConfigurationFormData, ConfigurationFilterStatus } from '../types';
import { transformFormDataToAPI } from '../utils/valueTransformations';

interface ConfigurationsResponse {
  success: boolean;
  data: ReportConfiguration[];
}

interface SingleConfigResponse {
  success: boolean;
  data: ReportConfiguration;
}

export const useReportConfigurations = (brandId?: number, statusFilter: ConfigurationFilterStatus = 'all') => {
  return useQuery<ReportConfiguration[], Error>({
    queryKey: ['report-configuration', 'list', brandId, statusFilter],
    queryFn: async () => {
      const endpoint = brandId
        ? API_ENDPOINTS.CONFIGURATION_BY_BRAND(brandId)
        : API_ENDPOINTS.CONFIGURATIONS;
      
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const queryString = params.toString();
      const url = queryString 
        ? `${REPORT_CONFIG_BASE_URL}${endpoint}?${queryString}`
        : `${REPORT_CONFIG_BASE_URL}${endpoint}`;
      
      const { data } = await apiClient.get<ConfigurationsResponse>(url);
      return data.data || [];
    },
    enabled: !!brandId,
  });
};

export const useReportConfiguration = (id: number) => {
  return useQuery<ReportConfiguration, Error>({
    queryKey: ['report-configuration', 'detail', id],
    queryFn: async () => {
      const { data } = await apiClient.get<SingleConfigResponse>(
        `${REPORT_CONFIG_BASE_URL}${API_ENDPOINTS.CONFIGURATION_BY_ID(id)}`
      );
      return data.data;
    },
    enabled: !!id,
  });
};

interface CreateConfigPayload extends Omit<ConfigurationFormData, 'brand_id'> {
  brand_id: number;
  brand_name?: string;
  template_id: number;
}

export const useCreateConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateConfigPayload) => {
      const apiPayload = {
        ...transformFormDataToAPI({
          platform: payload.platform,
          report_type: payload.report_type,
          granularity_level: payload.granularity_level,
          metric_type: payload.metric_type,
          cadence: payload.cadence,
          breakdown: payload.breakdown,
        }),
        brand_id: payload.brand_id,
        template_id: payload.template_id,
        use_fiscal_calendar: payload.use_fiscal_calendar,
      };
      
      try {
        const { data } = await apiClient.post(
          `${REPORT_CONFIG_BASE_URL}${API_ENDPOINTS.CONFIGURATIONS}`,
          apiPayload
        );
        
        await logAdminAction(
          `Report configuration created for brand ID ${payload.brand_id}`,
          {
            url: `${REPORT_CONFIG_BASE_URL}${API_ENDPOINTS.CONFIGURATIONS}`,
            method: 'POST',
            status: 200,
            category: 'report',
            action: 'create_report_config',
            brandId: payload.brand_id,
            platform: payload.platform,
            reportType: payload.report_type,
            details: [
              { label: 'Brand ID', value: formatIdWithName(payload.brand_id, payload.brand_name) },
              ...buildDetails(
                {
                  platform: payload.platform,
                  report_type: payload.report_type,
                  granularity_level: payload.granularity_level,
                  metric_type: payload.metric_type,
                  cadence: payload.cadence,
                  breakdown: payload.breakdown,
                  use_fiscal_calendar: payload.use_fiscal_calendar,
                },
                REPORT_CONFIG_FIELD_SPECS,
                { skipEmpty: true }
              ),
            ],
          }
        );
        
        return data;
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Unable to save this configuration. Please try again.';
        await logAdminAction(`Failed to create report config: ${errorMessage}`, { category: 'report' }, true);
        throw new Error(errorMessage);
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['report-configuration', 'list', variables.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['report-configuration', 'list'] });
    },
  });
};

interface UpdateConfigPayload {
  id: number;
  brand_id: number;
  brand_name?: string;
  data: Partial<ConfigurationFormData>;
  /** Snapshot of the config before editing, used to log before -> after changes. */
  previous?: Partial<ConfigurationFormData>;
}

export const useUpdateConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, brand_id, brand_name, data, previous }: UpdateConfigPayload) => {
      const apiPayload: Record<string, unknown> = {};
      
      if (data.platform !== undefined) apiPayload.platform = data.platform;
      if (data.report_type !== undefined) apiPayload.report_type = data.report_type;
      if (data.granularity_level !== undefined) apiPayload.granularity_level = data.granularity_level;
      if (data.metric_type !== undefined) apiPayload.metric_type = data.metric_type;
      if (data.cadence !== undefined) apiPayload.cadence = data.cadence;
      if (data.use_fiscal_calendar !== undefined) apiPayload.use_fiscal_calendar = data.use_fiscal_calendar;

      try {
        const response = await apiClient.put(
          `${REPORT_CONFIG_BASE_URL}${API_ENDPOINTS.CONFIGURATION_BY_ID(id)}`,
          apiPayload
        );
        
        // Only diff the fields that were actually sent in this update.
        const changedSpecs = REPORT_CONFIG_FIELD_SPECS.filter(
          spec => apiPayload[spec.key] !== undefined
        );
        const changes = previous
          ? diffFields(previous, apiPayload, changedSpecs)
          : [];
        const changeSummary = changes
          .map(c => `${c.label}: ${c.from} → ${c.to}`)
          .join('; ');
        await logAdminAction(
          `Report configuration ID ${id} updated${
            changeSummary
              ? ` — ${changeSummary}`
              : changedSpecs.length
                ? ` (${changedSpecs.map(s => s.label).join(', ')})`
                : ''
          }`,
          {
            url: `${REPORT_CONFIG_BASE_URL}${API_ENDPOINTS.CONFIGURATION_BY_ID(id)}`,
            method: 'PUT',
            status: 200,
            category: 'report',
            action: 'update_report_config',
            configId: id,
            brandId: brand_id,
            changes,
            details: [
              { label: 'Configuration ID', value: String(id) },
              { label: 'Brand ID', value: formatIdWithName(brand_id, brand_name) },
            ],
          }
        );
        
        return response.data;
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || error.message || 'Failed to update configuration';
        await logAdminAction(`Failed to update report config ${id}: ${errorMessage}`, { category: 'report' }, true);
        throw new Error(errorMessage);
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['report-configuration', 'list', variables.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['report-configuration', 'detail', variables.id] });
    },
  });
};

interface DeactivatePayload {
  id: number;
  brand_id: number;
  brand_name?: string;
  deactivate_now: boolean;
  deactivation_at?: string;
}

export const useDeactivateConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, brand_id, brand_name, deactivate_now, deactivation_at }: DeactivatePayload) => {
      const payload: { deactivate_now: boolean; deactivation_at?: string } = {
        deactivate_now,
      };
      
      if (!deactivate_now && deactivation_at) {
        payload.deactivation_at = deactivation_at;
      }

      try {
        const { data } = await apiClient.post(
          `${REPORT_CONFIG_BASE_URL}${API_ENDPOINTS.DEACTIVATE(id)}`,
          payload
        );
        
        await logAdminAction(
          `Report configuration ID ${id} deactivated`,
          {
            url: `${REPORT_CONFIG_BASE_URL}${API_ENDPOINTS.DEACTIVATE(id)}`,
            method: 'POST',
            status: 200,
            category: 'report',
            action: 'deactivate_report_config',
            configId: id,
            brandId: brand_id,
            deactivateNow: deactivate_now,
            details: [
              { label: 'Configuration ID', value: String(id) },
              { label: 'Brand ID', value: formatIdWithName(brand_id, brand_name) },
              {
                label: 'When',
                value: deactivate_now
                  ? 'Immediately'
                  : deactivation_at
                  ? humanizeValue(deactivation_at, { type: 'date' })
                  : 'Not Set',
              },
            ],
          }
        );
        
        return data;
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || error.message || 'Failed to deactivate configuration';
        await logAdminAction(`Failed to deactivate report config ${id}: ${errorMessage}`, { category: 'report' }, true);
        throw new Error(errorMessage);
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['report-configuration', 'list', variables.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['report-configuration', 'detail', variables.id] });
    },
  });
};

export const useDeleteConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, brand_id, brand_name }: { id: number; brand_id: number; brand_name?: string }) => {
      try {
        const { data } = await apiClient.delete(
          `${REPORT_CONFIG_BASE_URL}${API_ENDPOINTS.CONFIGURATION_BY_ID(id)}`
        );
        
        await logAdminAction(
          `Report configuration ID ${id} deleted`,
          {
            url: `${REPORT_CONFIG_BASE_URL}${API_ENDPOINTS.CONFIGURATION_BY_ID(id)}`,
            method: 'DELETE',
            status: 200,
            category: 'report',
            action: 'delete_report_config',
            configId: id,
            brandId: brand_id,
            details: [
              { label: 'Configuration ID', value: String(id) },
              { label: 'Brand ID', value: formatIdWithName(brand_id, brand_name) },
            ],
          }
        );
        
        return data;
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || error.message || 'Failed to delete configuration';
        await logAdminAction(`Failed to delete report config ${id}: ${errorMessage}`, { category: 'report' }, true);
        throw new Error(errorMessage);
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['report-configuration', 'list', variables.brand_id] });
    },
  });
};

interface ActivatePayload {
  id: number;
  brand_id: number;
  brand_name?: string;
  payload?: {
    cancel_scheduled?: boolean;
    reactivation_at?: string;
  };
}

export const useActivateConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, brand_id, brand_name, payload = {} }: ActivatePayload) => {
      try {
        const { data } = await apiClient.post(
          `${REPORT_CONFIG_BASE_URL}${API_ENDPOINTS.ACTIVATE(id)}`,
          payload
        );
        
        await logAdminAction(
          `Report configuration ID ${id} activated`,
          {
            url: `${REPORT_CONFIG_BASE_URL}${API_ENDPOINTS.ACTIVATE(id)}`,
            method: 'POST',
            status: 200,
            category: 'report',
            action: 'activate_report_config',
            configId: id,
            brandId: brand_id,
            details: [
              { label: 'Configuration ID', value: String(id) },
              { label: 'Brand ID', value: formatIdWithName(brand_id, brand_name) },
              ...(payload.reactivation_at
                ? [{ label: 'Reactivation', value: humanizeValue(payload.reactivation_at, { type: 'date' }) }]
                : []),
            ],
          }
        );
        
        return data;
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || error.message || 'Failed to activate configuration';
        await logAdminAction(`Failed to activate report config ${id}: ${errorMessage}`, { category: 'report' }, true);
        throw new Error(errorMessage);
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['report-configuration', 'list', variables.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['report-configuration', 'detail', variables.id] });
    },
  });
};
